/**
 * Multiple-choice probe options.
 *
 * Option ORDER is the server's, and it is reshuffled every time (spec 5) — this
 * component must never sort, group or otherwise stabilise it, or players learn
 * "it's the third one" and the game breaks into container knowledge.
 *
 * PICTURE OPTIONS carry no text at all. A flag option used to render an `<img>`
 * pointed at `https://flagcdn.com/hu.svg` beside a `🇭🇺` label, and since the
 * prompt names the target country, either one WAS `correctIndex` — readable off
 * the DOM without looking at a single pixel. Now the artwork arrives as a
 * `blob:` object URL minted from bytes the gateway sealed, and `label` is empty.
 * Nothing in this markup names a country.
 *
 * `alt` is empty for the same reason: an accessible name here would be the
 * answer read aloud. The button is the control; the picture is its face.
 *
 * Hit targets are 56px tall on purpose: the phone case is the primary one.
 */

import type { RoundPayloadOption } from '@geogame/shared';

export interface OptionGridProps {
  readonly options: readonly RoundPayloadOption[];
  /**
   * Object URLs by option index, for a rung whose options are pictures. Absent
   * until the rung's key has been released and its sealed bundle opened.
   */
  readonly imageUrls?: readonly string[];
  readonly disabled: boolean;
  readonly chosenIdx: number | null;
  /**
   * CHANGE 3. The right answer, for a rung that is OVER.
   *
   * Only ever non-null when the caller holds an `AnswerReveal`, which cannot be
   * minted for a rung still in play — so this prop cannot be populated while it
   * would matter. When it is set, the grid stops being a control and becomes
   * the reveal: green on the answer, red on what the player actually picked.
   */
  readonly correctIdx?: number | null;
  readonly onChoose: (idx: number) => void;
}

function optionState(idx: number, chosenIdx: number | null, correctIdx: number | null): string {
  if (correctIdx === null) return chosenIdx === idx ? 'chosen' : 'idle';
  if (idx === correctIdx) return 'correct';
  if (idx === chosenIdx) return 'wrong';
  return 'dimmed';
}

export function OptionGrid({
  options,
  imageUrls,
  disabled,
  chosenIdx,
  correctIdx = null,
  onChoose,
}: OptionGridProps): React.JSX.Element {
  const hasImages = imageUrls !== undefined && imageUrls.length > 0;
  const revealing = correctIdx !== null;
  return (
    <div className={hasImages ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2 sm:grid-cols-2'}>
      {options.map((option, idx) => {
        const url = imageUrls?.[idx];
        return (
          <button
            key={option.id}
            type="button"
            disabled={disabled || revealing}
            onClick={() => onChoose(idx)}
            className="gg-option"
            data-chosen={chosenIdx === idx ? 'true' : 'false'}
            data-state={optionState(idx, chosenIdx, correctIdx)}
          >
            {url !== undefined ? (
              <img
                src={url}
                alt=""
                className="h-16 w-full rounded object-cover"
                loading="eager"
                decoding="async"
              />
            ) : null}
            {option.label === '' ? null : <span className="gg-option-label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
